import React from 'react';
import { v4 as uuid } from 'uuid';
import { Page, useCareplusRouter } from '../../router/context';
import ModalTimeNotAvailable from '../../../../components/ModalTimeNotAvailable';
import { ProductSlotsTemplate } from '../../../../templates/ProductSlotsTemplate';
import { DateSlots } from '../../../../templates/ProductSlotsTemplate/components/ProductSlots';
import { toast } from 'react-toastify';
import { useSpecialist } from '../../store/Specialist';
import { useDateTime } from '../../store/Datetime';
import { ModalSystemUnavailable } from '../../components/ModalSystemUnavailable';
import useOnCloseSchedule from '../../../../hooks/useOnCloseSchedule';
import { useSchedule } from '../../store/Schedule';
import { fetchSlots } from '../../../../modules/appointmentBook';
import { SystemUnavailableError } from '../../../../errors';
import { useUnits } from '../../store/Units';
import { ATTENDANCE_TYPE, useAttendance } from '../../store/Attendance';
import { useAppointments } from '../../store/Appointment';
import { useSpecialties } from '../../store/Specialties';
import { envConfig } from '../../../../utils/enviroment';
import { isSameDay } from 'date-fns';

export function ProductSlotsPage() {
  const { navigate, goBack } = useCareplusRouter();
  const { specialistSelected } = useSpecialist();
  const { setSelectedDate } = useDateTime();
  const { selectedUnit } = useUnits();
  const { selectedAttendance, isFirstAppointment } = useAttendance();
  const { setSchedule, scheduleBuild, schedule } = useSchedule();
  const { selectedAppointment, setSelectedAppointment } = useAppointments();
  const { specialtySelected } = useSpecialties();

  const { onCloseSchedule } = useOnCloseSchedule({});

  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalSlotUnavailableVisible, setIsModalSlotUnavailableVisible] = React.useState(false);
  const [slotsHours, setSlotsHours] = React.useState<DateSlots[]>([]);

  const handleError = (error: Error) => {
    if (error instanceof SystemUnavailableError) setIsModalSystemUnavailableVisible(true);
  };

  const createSlotsPayload = (date: string) => {
    const appointmentReschedule = selectedAppointment;
    if (appointmentReschedule?.id)
      return {
        userId: appointmentReschedule.resource.externalId,
        catalogUnitId: appointmentReschedule.unit.externalId,
        catalogSpecialtyId: String(appointmentReschedule.resource.catalogId),
        interval: 60,
        startDate: date,
        service:
          appointmentReschedule?.service?.attendanceType?.id === ATTENDANCE_TYPE.PRESENTIAL
            ? 'PRESENTIAL'
            : 'TELEMEDICINE',
        appointmentType: 'FIRST_APPOINTMENT'
      };
    return {
      userId: String(specialistSelected.user_id),
      catalogUnitId: selectedUnit,
      catalogSpecialtyId: specialistSelected.id,
      interval: 60,
      startDate: date,
      service: selectedAttendance === ATTENDANCE_TYPE.PRESENTIAL ? 'PRESENTIAL' : 'TELEMEDICINE',
      appointmentType: isFirstAppointment ? 'FIRST_APPOINTMENT' : 'RETURN'
    };
  };

  const checkIfIsSpecialtyForSchedulingOverTwoDays = () =>
    envConfig.REACT_APP_CARE_PLUS_SPECIALTIES_SCHEDULING_FROM_TWO_DAYS?.includes(specialtySelected?.id ?? '');

  const addTwoDays = (date: Date) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 2);
    return newDate;
  };

  const setNewDate = (dateToChange: Date, date: Date) => {
    dateToChange.setMonth(date.getMonth());
    dateToChange.setDate(date.getDate());
    dateToChange.setFullYear(date.getFullYear());
  };

  const loadSlotsDates = async (baseDate: Date): Promise<DateSlots[]> => {
    const dateSearch = new Date();
    setNewDate(dateSearch, baseDate);

    if (isSameDay(baseDate, new Date()) && checkIfIsSpecialtyForSchedulingOverTwoDays()) {
      const dateFromTwoDays = addTwoDays(baseDate);

      setNewDate(dateSearch, dateFromTwoDays);
    }

    if (baseDate > new Date()) dateSearch.setDate(1);

    try {
      const sequentialDates: DateSlots[] = [];

      const result = await fetchSlots(createSlotsPayload(dateSearch.toISOString().substring(0, 10)));

      if (result.length) {
        result.map(item => {
          sequentialDates.push({ id: uuid(), date: item.date, slot: { hours: item.hours, duration: item.duration } });
        });

        setSlotsHours(r => [...r, ...sequentialDates]);
        return sequentialDates;
      }
      toast.error('Não há horários disponíveis.');
      return [];
    } catch (error: any) {
      handleError(error);
      return [];
    }
  };

  const loadSlotsHours = async (date: Date): Promise<DateSlots[]> => {
    const slots = slotsHours.find(item => item.date === date.toISOString().split('T')[0]);

    return (
      slots?.slot.hours.map((hour: any) => ({
        id: uuid(),
        date: `${slots.date}T${hour}`,
        slot: { duration: slots.slot.duration }
      })) || []
    );
  };

  const submit = async (slotSelected: DateSlots) => {
    const [slotDate, slotHour] = slotSelected.date.split('T');

    setSchedule({
      date: slotDate,
      hour: slotHour.slice(0, 5),
      duration: slotSelected?.slot?.duration,
      observations: ''
    });
    scheduleBuild();

    setSelectedAppointment({
      ...selectedAppointment,
      selectedDate: slotDate,
      selectedHour: slotHour,
      date: `${slotDate}T${slotHour}`
    });

    setSelectedDate(new Date(slotSelected.date));
    navigate(Page.APPOINTMENT_CONFIRMATION);
  };

  return (
    <>
      <ProductSlotsTemplate
        headTitle="Escolha uma data e horário"
        goBack={goBack}
        handleSlot={submit}
        loadSlotsDates={loadSlotsDates}
        loadSlotsHours={loadSlotsHours}
        handleSelectSlot={_ => {}}
        customSlotsHead
        productName={specialistSelected?.name ?? ''}
        serviceName=""
        productId=""
      />

      <ModalSystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
        callToSupport={() => (location.href = 'https://wa.me/11964079032?whatsapp=11964079032')}
        goToHome={onCloseSchedule}
      />

      <ModalTimeNotAvailable
        visible={isModalSlotUnavailableVisible}
        onClose={() => setIsModalSlotUnavailableVisible(false)}
      />
    </>
  );
}
